import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { timesheetsApi, Timesheet } from '../../api/timesheetsApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import { stringToColor, getInitials, formatDate } from '../../utils/helpers';
import { EditTimesheetModal } from './EditTimesheetModal';
import './Approvals.css';

// Employee-level grouping
interface EmployeeGroup {
  employee_id: number;
  employee_name: string;
  employee_code: string | null;
  timesheets: Timesheet[];
  total_hours: number;
}

// Department-level grouping (contains employees)
interface DepartmentGroup {
  department_id: number | null;
  department_name: string;
  employees: EmployeeGroup[];
  total_entries: number;
  total_hours: number;
}

const DEPARTMENTS_PER_PAGE = 10;

export function Approvals() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [rejectModal, setRejectModal] = useState<{ id: number; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editModal, setEditModal] = useState<Timesheet | null>(null);
  
  // State for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // State for collapsed departments and employees (default: all collapsed)
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Clear selections on refresh to avoid stale state
      setSelectedIds(new Set());
      const response = await timesheetsApi.getPendingApproval();
      setTimesheets(response.timesheets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  const handleApprove = async (timesheetId: number) => {
    try {
      setProcessing(prev => new Set(prev).add(timesheetId));
      await timesheetsApi.approveTimesheet(timesheetId);
      setTimesheets(prev => prev.filter(t => t.id !== timesheetId));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(timesheetId);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve timesheet');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(timesheetId);
        return next;
      });
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      setProcessing(prev => new Set(prev).add(rejectModal.id));
      await timesheetsApi.rejectTimesheet(rejectModal.id, rejectReason);
      setTimesheets(prev => prev.filter(t => t.id !== rejectModal.id));
      setRejectModal(null);
      setRejectReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject timesheet');
    } finally {
      if (rejectModal) {
        setProcessing(prev => {
          const next = new Set(prev);
          next.delete(rejectModal.id);
          return next;
        });
      }
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    try {
      const ids: number[] = [];
      selectedIds.forEach(id => ids.push(id));
      setProcessing(new Set(ids));
      await timesheetsApi.bulkApprove(ids);
      setTimesheets(prev => prev.filter(t => !selectedIds.has(t.id)));
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk approve');
    } finally {
      setProcessing(new Set());
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === timesheets.length && timesheets.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(timesheets.map(t => t.id)));
    }
  };

  // Toggle selection for all timesheets of a specific employee
  const toggleEmployeeSelection = useCallback((employeeTimesheetIds: number[]) => {
    setSelectedIds(prev => {
      const allSelected = employeeTimesheetIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        employeeTimesheetIds.forEach(id => next.delete(id));
      } else {
        employeeTimesheetIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, []);

  // Toggle selection for all timesheets in a department
  const toggleDepartmentSelection = useCallback((departmentTimesheetIds: number[]) => {
    setSelectedIds(prev => {
      const allSelected = departmentTimesheetIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        departmentTimesheetIds.forEach(id => next.delete(id));
      } else {
        departmentTimesheetIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, []);

  // Check if all timesheets for an employee are selected
  const areAllEmployeeTimesheetsSelected = useCallback((employeeTimesheetIds: number[]) => {
    return employeeTimesheetIds.length > 0 && employeeTimesheetIds.every(id => selectedIds.has(id));
  }, [selectedIds]);

  // Check if some (but not all) timesheets for an employee are selected
  const areSomeEmployeeTimesheetsSelected = useCallback((employeeTimesheetIds: number[]) => {
    const selectedCount = employeeTimesheetIds.filter(id => selectedIds.has(id)).length;
    return selectedCount > 0 && selectedCount < employeeTimesheetIds.length;
  }, [selectedIds]);

  // Toggle expand state for a department
  const toggleDepartmentExpand = useCallback((deptKey: string) => {
    setExpandedDepartments(prev => {
      const next = new Set(prev);
      if (next.has(deptKey)) {
        next.delete(deptKey);
      } else {
        next.add(deptKey);
      }
      return next;
    });
  }, []);

  // Toggle expand state for an employee (key = "deptId:empId")
  const toggleEmployeeExpand = useCallback((empKey: string) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(empKey)) {
        next.delete(empKey);
      } else {
        next.add(empKey);
      }
      return next;
    });
  }, []);

  // Group timesheets by department, then by employee
  const departmentGroups: DepartmentGroup[] = useMemo(() => {
    const deptMap: Record<string, DepartmentGroup> = {};
    
    for (const ts of timesheets) {
      const deptKey = String(ts.department_id ?? 'null');
      const deptName = ts.department_name || 'No Department';
      
      if (!deptMap[deptKey]) {
        deptMap[deptKey] = {
          department_id: ts.department_id,
          department_name: deptName,
          employees: [],
          total_entries: 0,
          total_hours: 0,
        };
      }
      
      // Find or create employee group within department
      let empGroup = deptMap[deptKey].employees.find(e => e.employee_id === ts.employee_id);
      if (!empGroup) {
        empGroup = {
          employee_id: ts.employee_id,
          employee_name: ts.employee_name || 'Unknown',
          employee_code: ts.employee_code,
          timesheets: [],
          total_hours: 0,
        };
        deptMap[deptKey].employees.push(empGroup);
      }
      
      empGroup.timesheets.push(ts);
      empGroup.total_hours += ts.hours;
      deptMap[deptKey].total_entries += 1;
      deptMap[deptKey].total_hours += ts.hours;
    }
    
    // Sort departments alphabetically, then employees within each department
    const sorted = Object.values(deptMap).sort((a, b) => a.department_name.localeCompare(b.department_name));
    for (const dept of sorted) {
      dept.employees.sort((a, b) => a.employee_name.localeCompare(b.employee_name));
    }
    
    return sorted;
  }, [timesheets]);

  // Collapse all departments and employees
  const collapseAll = useCallback(() => {
    setExpandedDepartments(new Set());
    setExpandedEmployees(new Set());
  }, []);

  // Expand all departments and employees
  const expandAll = useCallback(() => {
    const allDeptKeys = departmentGroups.map(d => String(d.department_id ?? 'null'));
    const allEmpKeys: string[] = [];
    for (const dept of departmentGroups) {
      const deptKey = String(dept.department_id ?? 'null');
      for (const emp of dept.employees) {
        allEmpKeys.push(`${deptKey}:${emp.employee_id}`);
      }
    }
    setExpandedDepartments(new Set(allDeptKeys));
    setExpandedEmployees(new Set(allEmpKeys));
  }, [departmentGroups]);

  // Filter department groups by search term (searches employee name/code)
  const filteredDepartmentGroups = useMemo(() => {
    if (!searchTerm.trim()) return departmentGroups;
    
    const term = searchTerm.toLowerCase().trim();
    
    // Filter departments that have matching employees
    return departmentGroups
      .map(dept => {
        const matchingEmployees = dept.employees.filter(emp =>
          emp.employee_name.toLowerCase().includes(term) ||
          (emp.employee_code && emp.employee_code.toLowerCase().includes(term))
        );
        
        if (matchingEmployees.length === 0) return null;
        
        return {
          ...dept,
          employees: matchingEmployees,
          total_entries: matchingEmployees.reduce((sum, e) => sum + e.timesheets.length, 0),
          total_hours: matchingEmployees.reduce((sum, e) => sum + e.total_hours, 0),
        };
      })
      .filter((d): d is DepartmentGroup => d !== null);
  }, [departmentGroups, searchTerm]);

  // Paginate filtered department groups
  const totalPages = Math.ceil(filteredDepartmentGroups.length / DEPARTMENTS_PER_PAGE);
  const paginatedDepartmentGroups = useMemo(() => {
    const start = (currentPage - 1) * DEPARTMENTS_PER_PAGE;
    return filteredDepartmentGroups.slice(start, start + DEPARTMENTS_PER_PAGE);
  }, [filteredDepartmentGroups, currentPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate stats
  const totalEmployees = departmentGroups.reduce((sum, d) => sum + d.employees.length, 0);
  const filteredTimesheetCount = filteredDepartmentGroups.reduce((sum, d) => sum + d.total_entries, 0);

  const totalPending = timesheets.length;
  const totalHours = timesheets.reduce((sum, t) => sum + t.hours, 0);

  return (
    <div className="approvals-page">
      <header className="approvals-header">
        <div className="approvals-title">
          <h1>Timesheet Approvals</h1>
          <p>Review and approve submitted timesheets</p>
        </div>
        {selectedIds.size > 0 && (
          <Button onClick={handleBulkApprove} disabled={processing.size > 0}>
            <Icons.Check />
            Approve Selected ({selectedIds.size})
          </Button>
        )}
      </header>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="approvals-stats">
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
            <Icons.Clock />
          </div>
          <div className="stat-info">
            <span className="stat-label">Pending</span>
            <span className="stat-value">{totalPending}</span>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}>
            <Icons.Building />
          </div>
          <div className="stat-info">
            <span className="stat-label">Departments</span>
            <span className="stat-value">{departmentGroups.length}</span>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
            <Icons.Users />
          </div>
          <div className="stat-info">
            <span className="stat-label">Employees</span>
            <span className="stat-value">{totalEmployees}</span>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
            <Icons.FileText />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Hours</span>
            <span className="stat-value">{totalHours.toFixed(1)}</span>
          </div>
        </Card>
      </div>

      {departmentGroups.length === 0 ? (
        <Card className="empty-state">
          <Icons.Check />
          <h3>All Caught Up!</h3>
          <p>No timesheets pending approval.</p>
        </Card>
      ) : (
        <Card className="approvals-list-card">
          {/* Search Bar */}
          <div className="approvals-search-bar">
            <div className="search-input-wrapper">
              <Icons.Search className="search-icon" />
              <input
                type="text"
                placeholder="Search by employee name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <Icons.X />
                </button>
              )}
            </div>
            {searchTerm && (
              <span className="search-results-count">
                {filteredDepartmentGroups.length} department{filteredDepartmentGroups.length !== 1 ? 's' : ''} found 
                ({filteredTimesheetCount} timesheet{filteredTimesheetCount !== 1 ? 's' : ''})
              </span>
            )}
          </div>

          <div className="approvals-toolbar">
            <label className="select-all">
              <input
                type="checkbox"
                checked={selectedIds.size === timesheets.length && timesheets.length > 0}
                onChange={toggleSelectAll}
              />
              <span>Select All</span>
            </label>
            <div className="toolbar-actions">
              <div className="collapse-actions">
                <button
                  className="collapse-btn"
                  onClick={expandAll}
                  title="Expand all"
                >
                  <Icons.ChevronsDown />
                  Expand All
                </button>
                <button
                  className="collapse-btn"
                  onClick={collapseAll}
                  title="Collapse all"
                >
                  <Icons.ChevronsUp />
                  Collapse All
                </button>
              </div>
              <button 
                className={`refresh-btn ${loading ? 'refreshing' : ''}`} 
                onClick={fetchPendingApprovals}
                disabled={loading}
                title="Refresh to get latest pending approvals"
              >
                <Icons.Refresh />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {filteredDepartmentGroups.length === 0 ? (
            <div className="no-results">
              <Icons.Search />
              <p>No employees match "{searchTerm}"</p>
              <button onClick={() => setSearchTerm('')}>Clear search</button>
            </div>
          ) : (
            <>
              <div className="approvals-groups">
                {paginatedDepartmentGroups.map((dept) => {
                  const deptKey = String(dept.department_id ?? 'null');
                  const isDeptExpanded = expandedDepartments.has(deptKey);
                  
                  // Get all timesheet IDs in this department for selection
                  const deptTimesheetIds = dept.employees.flatMap(e => e.timesheets.map(t => t.id));
                  const allDeptSelected = deptTimesheetIds.length > 0 && deptTimesheetIds.every(id => selectedIds.has(id));
                  const someDeptSelected = deptTimesheetIds.some(id => selectedIds.has(id)) && !allDeptSelected;

                  return (
                    <div key={deptKey} className="department-group">
                      {/* Department Header */}
                      <div className="department-group-header">
                        <div className="department-header-left">
                          <button
                            className="collapse-toggle"
                            onClick={() => toggleDepartmentExpand(deptKey)}
                            title={isDeptExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isDeptExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                          </button>
                          <div className="department-info">
                            <Icons.Building className="dept-icon" />
                            <div>
                              <span className="department-name">{dept.department_name}</span>
                              <span className="department-summary">
                                {dept.employees.length} {dept.employees.length === 1 ? 'employee' : 'employees'} • 
                                {dept.total_entries} {dept.total_entries === 1 ? 'entry' : 'entries'} • 
                                {dept.total_hours.toFixed(1)}h
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="group-actions">
                          <label className={`dept-select-all ${someDeptSelected ? 'indeterminate' : ''}`}>
                            <input
                              type="checkbox"
                              checked={allDeptSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someDeptSelected;
                              }}
                              onChange={() => toggleDepartmentSelection(deptTimesheetIds)}
                            />
                            <span>{allDeptSelected ? 'Deselect All' : 'Select All'}</span>
                          </label>
                        </div>
                      </div>

                      {/* Employees within Department */}
                      {isDeptExpanded && (
                        <div className="employees-container">
                          {dept.employees.map((emp) => {
                            const empKey = `${deptKey}:${emp.employee_id}`;
                            const isEmpExpanded = expandedEmployees.has(empKey);
                            const employeeTimesheetIds = emp.timesheets.map(t => t.id);
                            const allEmpSelected = areAllEmployeeTimesheetsSelected(employeeTimesheetIds);
                            const someEmpSelected = areSomeEmployeeTimesheetsSelected(employeeTimesheetIds);

                            return (
                              <div key={emp.employee_id} className={`employee-group ${isEmpExpanded ? 'expanded' : ''}`}>
                                <div className="employee-group-header">
                                  <div className="employee-header-left">
                                    <button
                                      className="collapse-toggle"
                                      onClick={() => toggleEmployeeExpand(empKey)}
                                      title={isEmpExpanded ? 'Collapse' : 'Expand'}
                                    >
                                      {isEmpExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                                    </button>
                                    <div className="employee-info">
                                      <div
                                        className="avatar"
                                        style={{ backgroundColor: stringToColor(emp.employee_name) }}
                                      >
                                        {getInitials(emp.employee_name.split(' ')[0], emp.employee_name.split(' ')[1] || '')}
                                      </div>
                                      <div>
                                        <span className="employee-name">
                                          {emp.employee_name}
                                          {emp.employee_code && <span className="employee-code">({emp.employee_code})</span>}
                                        </span>
                                        <span className="employee-summary">
                                          {emp.timesheets.length} {emp.timesheets.length === 1 ? 'entry' : 'entries'} • {emp.total_hours.toFixed(1)}h
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="group-actions">
                                    <label className={`employee-select-all ${someEmpSelected ? 'indeterminate' : ''}`}>
                                      <input
                                        type="checkbox"
                                        checked={allEmpSelected}
                                        ref={(el) => {
                                          if (el) el.indeterminate = someEmpSelected;
                                        }}
                                        onChange={() => toggleEmployeeSelection(employeeTimesheetIds)}
                                      />
                                      <span>{allEmpSelected ? 'Deselect All' : 'Select All'}</span>
                                    </label>
                                  </div>
                                </div>

                                {/* Timesheets Table */}
                                {isEmpExpanded && (
                                  <table className="timesheets-table">
                                    <thead>
                                      <tr>
                                        <th className="checkbox-col"></th>
                                        <th>Date</th>
                                        <th>Project</th>
                                        <th>Task</th>
                                        <th>Hours</th>
                                        <th>Description</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {emp.timesheets.map((ts) => (
                                        <tr key={ts.id} className={selectedIds.has(ts.id) ? 'selected' : ''}>
                                          <td className="checkbox-col">
                                            <input
                                              type="checkbox"
                                              checked={selectedIds.has(ts.id)}
                                              onChange={() => toggleSelect(ts.id)}
                                            />
                                          </td>
                                          <td>{formatDate(ts.date)}</td>
                                          <td>{ts.project_name || '-'}</td>
                                          <td>{ts.task_type_name || '-'}</td>
                                          <td><strong>{ts.hours}h</strong></td>
                                          <td className="description-cell">
                                            {ts.description || <span className="no-desc">No description</span>}
                                          </td>
                                          <td className="actions-cell">
                                            <button
                                              className="action-btn edit"
                                              onClick={() => setEditModal(ts)}
                                              disabled={processing.has(ts.id)}
                                              title="Edit"
                                            >
                                              <Icons.Edit />
                                            </button>
                                            <button
                                              className="action-btn approve"
                                              onClick={() => handleApprove(ts.id)}
                                              disabled={processing.has(ts.id)}
                                              title="Approve"
                                            >
                                              {processing.has(ts.id) ? <span className="spinner"></span> : <Icons.Check />}
                                            </button>
                                            <button
                                              className="action-btn reject"
                                              onClick={() => setRejectModal({ id: ts.id, name: emp.employee_name })}
                                              disabled={processing.has(ts.id)}
                                              title="Reject"
                                            >
                                              <Icons.X />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="approvals-pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First page"
                  >
                    <Icons.ChevronsLeft />
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <Icons.ChevronLeft />
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                    <span className="pagination-detail">
                      (Showing {paginatedDepartmentGroups.length} of {filteredDepartmentGroups.length} departments)
                    </span>
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    title="Next page"
                  >
                    <Icons.ChevronRight />
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    title="Last page"
                  >
                    <Icons.ChevronsRight />
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Timesheet</h3>
              <button className="modal-close" onClick={() => setRejectModal(null)}>
                <Icons.X />
              </button>
            </div>
            <div className="modal-body">
              <p>You are about to reject this timesheet entry from <strong>{rejectModal.name}</strong>.</p>
              <label className="form-label">
                Reason (optional)
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                  rows={3}
                />
              </label>
            </div>
            <div className="modal-footer">
              <Button variant="outline" onClick={() => setRejectModal(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleReject} disabled={processing.has(rejectModal.id)}>
                {processing.has(rejectModal.id) ? 'Rejecting...' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <EditTimesheetModal
          timesheet={editModal}
          onClose={() => setEditModal(null)}
          onSave={() => {
            setEditModal(null);
            fetchPendingApprovals();
          }}
        />
      )}
    </div>
  );
}

export default Approvals;
